'use client';

import {
  Link as RouterLink,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import { CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';

import { useAccessContext } from '@/routing/useAccessContext';
import { canAccessAppRouteMeta } from '@/routing/access-meta';
import { useNavEntryLabel } from '@/routing/use-nav-entry-label';
import { extractNavEntriesFromRoutes } from '@/routing/nav-from-router';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';
import { buildPinnedGroup, PINNED_GROUP_ID } from '@/routing/pinned-nav-group';
import { useNavLayout } from '@/hooks/useNavLayout';
import { useSidebarPreferences } from '@/hooks/useSidebarPreferences';

function isPathMatch(base: string | undefined, path: string): boolean {
  if (!base) return false;
  const norm = (s: string) => (s === '/' ? '/' : s.replace(/\/+$/, ''));
  const b = norm(base);
  const p = norm(path);
  if (b === '/') return p === '/';
  return p === b || p.startsWith(`${b}/`);
}

function CollapsibleMenu({
  id,
  collapsed,
  children,
}: {
  id: string;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  // CSS transitions only animate on value changes, not the initial mount, so
  // the height animation plays when the user toggles a group but not on first
  // render. When collapsed, mark the subtree inert + aria-hidden so the
  // visually-hidden links leave the tab order and the a11y tree.
  return (
    <div
      id={id}
      className="overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
      style={{
        display: 'grid',
        gridTemplateRows: collapsed ? '0fr' : '1fr',
      }}
    >
      <div className="min-h-0" inert={collapsed} aria-hidden={collapsed}>
        {children}
      </div>
    </div>
  );
}

/**
 * Returns the longest nav path that matches the current location. Sidebar
 * groups highlight the item whose `fullPath` equals this, so a deeper child
 * route wins over its parent. Computed once across all items, not per group.
 */
function findBestMatch(
  items: RouterNavEntry[],
  pathname: string,
): string | null {
  let best: string | null = null;
  for (const item of items) {
    const path = item.fullPath ?? '';
    if (
      isPathMatch(item.fullPath, pathname) &&
      path.length > (best?.length ?? -1)
    ) {
      best = path;
    }
  }
  return best;
}

function NavGroup({
  group,
  bestMatch,
  collapsed,
  onToggle,
}: {
  group: SidebarNavGroup;
  bestMatch: string | null;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const contentId = `nav-group-${group.id}`;
  const navEntryLabel = useNavEntryLabel();
  return (
    <SidebarGroup>
      <SidebarGroupLabel asChild>
        <button
          type="button"
          className="group/label flex w-full items-center cursor-pointer select-none"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls={contentId}
        >
          <span>{group.title}</span>
          <CaretRightIcon
            style={{ width: 12, height: 12, minWidth: 12, minHeight: 12 }}
            className={cn(
              'shrink-0 ml-0.5 transition-all duration-200',
              collapsed ? 'rotate-0' : 'rotate-90',
              collapsed
                ? 'opacity-60'
                : 'opacity-0 group-hover/label:opacity-60',
            )}
            weight="bold"
          />
        </button>
      </SidebarGroupLabel>
      <CollapsibleMenu id={contentId} collapsed={collapsed}>
        <SidebarMenu>
          {group.items.map((item) => {
            const label = navEntryLabel(item);
            const isActive = item.fullPath === bestMatch;
            const icon = item.meta.nav?.icon;

            return (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton asChild tooltip={label} isActive={isActive}>
                  <RouterLink to={item.fullPath}>
                    {icon}
                    <span>{label}</span>
                  </RouterLink>
                </SidebarMenuButton>
                {item.meta.nav?.subItems}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </CollapsibleMenu>
    </SidebarGroup>
  );
}

export function RouterNavRoutes() {
  const { pathname } = useLocation();
  const router = useRouter();
  const accessCtx = useAccessContext();
  const { collapsedGroups, toggleGroup } = useSidebarPreferences();

  // React Compiler auto-memoizes these derived values, so they keep a stable
  // identity across renders (which is what lets useNavLayout's memo hold).
  const entries = extractNavEntriesFromRoutes(router.flatRoutes).filter((e) =>
    canAccessAppRouteMeta(e.meta, accessCtx),
  );

  const { pinnedItems, sections: layoutSections } = useNavLayout(entries);

  const { pinnedGroup, sections } = buildPinnedGroup(
    pinnedItems,
    layoutSections,
  );

  const allItems = [
    ...(pinnedGroup?.items ?? []),
    ...sections.flatMap((g) => g.items),
  ];

  // Computed once across all items, then shared with every group, rather than
  // re-scanned per group.
  const bestMatch = findBestMatch(allItems, pathname);

  return (
    <>
      {pinnedGroup && (
        <NavGroup
          group={pinnedGroup}
          bestMatch={bestMatch}
          collapsed={collapsedGroups.includes(PINNED_GROUP_ID)}
          onToggle={() => toggleGroup(PINNED_GROUP_ID)}
        />
      )}
      {sections.map((group) => (
        <NavGroup
          key={group.id}
          group={group}
          bestMatch={bestMatch}
          collapsed={collapsedGroups.includes(group.id)}
          onToggle={() => toggleGroup(group.id)}
        />
      ))}
    </>
  );
}
